import { forwardRef, AfterViewInit, Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BaseComponent } from '@ridango/core';

import { takeUntil } from 'rxjs/operators';

const date = new Date();

interface TimeValue {
    hour: number;
    minute: number;
}

@Component({
    selector: 'ridango-ui-time-input',
    templateUrl: './time-input.component.html',
    styleUrls: ['./time-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TimeInputComponent),
            multi: true
        }
    ]
})
export class TimeInputComponent
    extends BaseComponent
    implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor
{
    @Input() set minDate(value: Date) {
        if (value) {
            this.minTime = value;
            if (this.hour && this.hour.value <= value.getHours()) {
                this.updateHourControl(value.getHours());
            }
        } else {
            this.minTime = null;
        }
    }
    minTime: Date;

    hour: FormControl;
    minute: FormControl;

    hourString: string;
    minuteString: string;

    disabled = false;

    value: TimeValue;

    onChange: any = () => {};
    onTouched: any = () => {};

    constructor() {
        super();
    }

    ngOnInit() {
        const hours = this.minTime ? this.minTime.getHours() : date.getHours();
        const minutes = this.minTime ? this.minTime.getMinutes() : date.getMinutes();
        this.hourString = this.formatString(hours);
        this.minuteString = this.formatString(minutes);

        this.hour = new FormControl({ value: this.hourString, disabled: this.disabled });
        this.minute = new FormControl({ value: this.minuteString, disabled: this.disabled });

        this.hour.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe((value) => {
            this.hourString = this.formatString(value);
            this.onChange(this.createValue());
        });
        this.minute.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe((value) => {
            this.minuteString = this.formatString(value);
            this.onChange(this.createValue());
        });
    }

    ngAfterViewInit() {
        if (this.value) {
            this.hourString = this.formatString(this.checkHour(this.value.hour));
            this.minuteString = this.formatString(this.checkMinute(this.value.minute));
        }

        setTimeout(() => {
            this.hour.setValue(this.hourString);
            this.minute.setValue(this.minuteString);
        });
    }

    writeValue(value: TimeValue): void {
        this.value = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private createValue(): TimeValue {
        return { hour: Number(this.hourString), minute: Number(this.minuteString) };
    }

    private formatString(value: number): string {
        return (value < 10 ? '0' : '') + value;
    }

    private checkHour(hour: number): number {
        return hour < 24 ? hour : 0;
    }

    private checkMinute(minute: number): number {
        return minute < 60 ? minute : 0;
    }

    validateHour(event) {
        const currentValue = event.srcElement.value;
        const checkedValue = this.checkHour(currentValue);

        if (currentValue !== checkedValue) {
            this.updateHourControl(checkedValue);
        }
    }

    validateMinute(event) {
        const currentValue = event.srcElement.value;
        const checkedValue = this.checkMinute(currentValue);

        if (currentValue !== checkedValue) {
            this.updateMinuteControl(checkedValue);
        }
    }

    increaseHour(): void {
        const newValue = Number(this.hourString) + 1;
        let next = newValue < 24 ? newValue : 0;
        if (this.minTime && next < this.minTime.getHours()) {
            next = this.minTime.getHours();
        }

        this.updateHourControl(next);
    }

    decreaseHour(): void {
        const newValue = Number(this.hourString) - 1;
        let next = newValue >= 0 ? newValue : 23;
        if (this.minTime && next < this.minTime.getHours()) {
            next = 23;
        }

        this.updateHourControl(next);
    }

    increaseMinutes(): void {
        const newValue = Number(this.minuteString) + 1;
        let next = newValue < 60 ? newValue : 0;
        if (this.minTime && this.hour.value == this.minTime.getHours() && next < this.minTime.getMinutes()) {
            next = this.minTime.getMinutes();
        }

        this.updateMinuteControl(next);
    }

    decreaseMinutes(): void {
        const newValue = Number(this.minuteString) - 1;
        let next = newValue >= 0 ? newValue : 59;
        if (this.minTime && this.hour.value == this.minTime.getHours() && next < this.minTime.getMinutes()) {
            next = 59;
        }

        this.updateMinuteControl(next);
    }

    private updateHourControl(hour: number): void {
        if (this.minTime && this.minTime.getHours() == hour && this.minute.value < this.minTime.getMinutes()) {
            this.updateMinuteControl(this.minTime.getMinutes());
        }
        this.hour.setValue(this.formatString(hour));
    }

    private updateMinuteControl(minute: number): void {
        this.minute.setValue(this.formatString(minute));
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
